/**
 * Shared primitives for the screens that have not been rebuilt on `src/ui/` yet.
 *
 * Button, Badge, Avatar, Modal and Skeleton are thin adapters over the new library, so
 * a screen that still imports them already renders in the new style. Card, PageHeader,
 * Segmented, Field, Table, Stat and the loaders keep their token-driven `k-*` classes
 * until their screens move; they are listed in the PR as the remaining legacy surface.
 */
import type { JSX, ReactNode } from "react";

import { Avatar as UiAvatar } from "@/ui/avatar";
import { Badge as UiBadge, type BadgeProps } from "@/ui/badge";
import { Button as UiButton, type ButtonProps } from "@/ui/button";
import { Dialog } from "@/ui/dialog";
import { Skeleton as UiSkeleton, type SkeletonSpec } from "@/ui/skeleton";
import { Spinner as UiSpinner } from "@/ui/spinner";

import { useTranslation } from "@/i18n";

import "./ui.css";

export type { SkeletonSpec };

export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string | undefined;
}): JSX.Element {
  return <section className={`k-card ${className}`}>{children}</section>;
}

/**
 * The page title and its actions. No subtitle: kapitan-ui-quality §2.2 keeps explanatory
 * sentences out of the title block, so there is no prop for one.
 */
export function PageHeader({
  title,
  actions,
}: {
  title: string;
  actions?: ReactNode | undefined;
}): JSX.Element {
  return (
    <header className="k-page-header">
      <div>
        <h1 className="k-page-title">{title}</h1>
      </div>
      {actions === undefined ? null : <div className="k-page-actions">{actions}</div>}
    </header>
  );
}

export type ButtonVariant = "primary" | "ghost" | "danger";

const BUTTON_VARIANT: Record<ButtonVariant, NonNullable<ButtonProps["variant"]>> = {
  primary: "primary",
  ghost: "ghost",
  danger: "destructive",
};

/** Legacy call sites pass `variant` as primary | ghost | danger. */
export function Button({
  children,
  onClick,
  variant = "primary",
  type = "button",
  disabled = false,
}: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  variant?: ButtonVariant | undefined;
  type?: "button" | "submit" | undefined;
  disabled?: boolean | undefined;
}): JSX.Element {
  return (
    <UiButton variant={BUTTON_VARIANT[variant]} type={type} disabled={disabled} onClick={onClick}>
      {children}
    </UiButton>
  );
}

/** Segmented control: the finance tabs and the payment filter chips. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  look = "tabs",
}: {
  options: readonly { value: T; label: string; badge?: number | undefined }[];
  value: T;
  onChange: (next: T) => void;
  look?: "tabs" | "chips" | undefined;
}): JSX.Element {
  return (
    <div className={`k-segmented k-segmented--${look}`} role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={`k-segmented__item ${option.value === value ? "is-active" : ""}`}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
          {option.badge !== undefined && option.badge > 0 ? (
            <span className="k-segmented__badge">{option.badge}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export type BadgeTone = "neutral" | "accent" | "positive" | "negative" | "warning" | "info";

const BADGE_TONE: Record<BadgeTone, NonNullable<BadgeProps["tone"]>> = {
  neutral: "neutral",
  // Brand-coloured tags are grey now: colour is for a state that needs action only.
  accent: "neutral",
  positive: "success",
  negative: "danger",
  warning: "warning",
  info: "info",
};

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: BadgeTone | undefined;
}): JSX.Element {
  return <UiBadge tone={BADGE_TONE[tone]}>{children}</UiBadge>;
}

export function Avatar({ name }: { name: string }): JSX.Element {
  return <UiAvatar name={name} />;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="k-field">
      <span className="k-field__label">{label}</span>
      {children}
      {error === undefined ? null : <span className="k-field__error">{error}</span>}
      {error === undefined && hint !== undefined ? (
        <span className="k-field__hint">{hint}</span>
      ) : null}
    </label>
  );
}

/** Legacy modal: the caller owns the open state by mounting it, so it is always open. */
export function Modal({
  title,
  children,
  onClose,
  footer,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode | undefined;
}): JSX.Element {
  return (
    <Dialog
      open
      title={title}
      footer={footer}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {children}
    </Dialog>
  );
}

/** A content placeholder. The shapes live in `src/ui/skeleton.tsx`. */
export function Skeleton({ spec }: { spec: SkeletonSpec | readonly SkeletonSpec[] }): JSX.Element {
  return <UiSkeleton spec={spec} />;
}

/** Inline activity indicator, for places too small to hold a placeholder shape. */
export function Spinner(): JSX.Element {
  return <UiSpinner />;
}

/**
 * The whole-window loader, used while the stored token is exchanged for the current
 * user. It repeats the pre-mount splash in `index.html` on purpose: the two are
 * consecutive frames of the same wait.
 */
export function PageLoader(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="k-pageLoader" role="status">
      <div className="k-pageLoader__brand">
        <span className="k-pageLoader__mark" aria-hidden="true">
          K
        </span>
        <span className="k-pageLoader__word">{t("app.name")}</span>
      </div>
      <span className="k-pageLoader__track" aria-hidden="true">
        <span className="k-pageLoader__indicator" />
      </span>
      <span className="k-visually-hidden">{t("common.loading")}</span>
    </div>
  );
}

/** Uniform loading / error / empty handling, so no screen invents its own. */
export function QueryState({
  isPending,
  isError,
  isEmpty,
  emptyLabel,
  onRetry,
  skeleton = { shape: "text" },
  children,
}: {
  isPending: boolean;
  isError: boolean;
  isEmpty?: boolean | undefined;
  emptyLabel?: string | undefined;
  onRetry?: (() => void) | undefined;
  skeleton?: SkeletonSpec | readonly SkeletonSpec[] | undefined;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();

  if (isPending) {
    return <Skeleton spec={skeleton} />;
  }
  if (isError) {
    return (
      <div className="k-state">
        <p className="k-state__error">{t("common.error")}</p>
        {onRetry === undefined ? null : (
          <Button variant="ghost" onClick={onRetry}>
            {t("common.retry")}
          </Button>
        )}
      </div>
    );
  }
  if (isEmpty === true) {
    return <p className="k-state k-muted">{emptyLabel ?? t("common.empty")}</p>;
  }
  return <>{children}</>;
}

/** A column heading. The object form exists so a numeric column's heading can sit
 *  over its right-aligned figures instead of drifting to the left of them. */
export type TableColumn = string | { label: string; align: "right" };

export function Table({
  head,
  children,
}: {
  head: readonly TableColumn[];
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="k-table__wrap">
      <table className="k-table">
        <thead>
          <tr>
            {head.map((column) => {
              const label = typeof column === "string" ? column : column.label;
              const align = typeof column === "string" ? undefined : column.align;
              return (
                <th key={label} className={align === "right" ? "k-table__th--right" : undefined}>
                  {label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Stat({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail?: string | undefined;
  tone?: "neutral" | "positive" | "negative" | undefined;
}): JSX.Element {
  return (
    <div className="k-stat k-card">
      <span className="k-stat__label">{label}</span>
      <strong className="k-stat__value">{value}</strong>
      {detail === undefined || detail === "" ? null : (
        <span className={`k-stat__detail k-stat__detail--${tone}`}>{detail}</span>
      )}
    </div>
  );
}

